import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'src/hooks/use-debounce';
import { useRedeemsStore } from 'src/store/redeems/redeems.store';

type RedeemsModalProps = {
  productHook: any;
};

type RedeemItem = {
  _id: string;
  name: string;
};

const DEFAULT_LIMIT = 10;

export const useRedeemsModal = ({ productHook }: RedeemsModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [partner, setPartner] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(0);
  const [pageChangedByPagination, setPageChangedByPagination] = useState(false);
  const [selectedRedeemId, setSelectedRedeemId] = useState('');
  const [selectedRedeemName, setSelectedRedeemName] = useState('');

  const debouncedName = useDebounce(name, 500);

  const [
    loadingFilteredRedeems,
    filteredRedeems,
    filteredRedeemsTotal,
    filterRedeems,
    resetFilteredRedeems,
  ] = useRedeemsStore((state) => [
    state.loadingFilteredRedeems,
    state.filteredRedeems,
    state.filteredRedeemsTotal,
    state.filterRedeems,
    state.resetFilteredRedeems,
  ]);

  const selectedPartner = useMemo(
    () => productHook?.part?.find((item: any) => item.value === partner),
    [productHook?.part, partner]
  );

  const reset = useCallback(() => {
    setPartner('');
    setName('');
    setPage(0);
    setPageChangedByPagination(false);
    setSelectedRedeemId('');
    setSelectedRedeemName('');
    resetFilteredRedeems();
  }, [resetFilteredRedeems]);

  const onClose = useCallback(() => {
    productHook.onCloseRedeemModal();
    reset();
  }, [productHook, reset]);

  const onSelectPartner = useCallback((partnerId: string) => {
    setPartner(partnerId);
    setName('');
    setPage(0);
    setPageChangedByPagination(false);
    setSelectedRedeemId('');
    setSelectedRedeemName('');
  }, []);

  const onChangeName = useCallback((value: string) => {
    setName(value);
    setPage(0);
    setPageChangedByPagination(false);
  }, []);

  const onChangePage = useCallback((newPage: number) => {
    setPageChangedByPagination(true);
    setPage(newPage);
  }, []);

  const onSelectRedeem = useCallback((redeem: RedeemItem) => {
    setSelectedRedeemId((currentId) => {
      const isAlreadySelected = currentId === redeem._id;
      setSelectedRedeemName(isAlreadySelected ? '' : redeem.name);
      return isAlreadySelected ? '' : redeem._id;
    });
  }, []);

  const onConfirm = useCallback(() => {
    if (!partner) {
      enqueueSnackbar('Selecione um parceiro', { variant: 'warning' });
      return;
    }

    if (!selectedRedeemId) {
      enqueueSnackbar('Selecione um resgatável', { variant: 'warning' });
      return;
    }

    const found = productHook?.allredeems?.find(
      (redeem: any) => redeem.value === selectedRedeemId || redeem._id === selectedRedeemId
    );

    if (found) {
      productHook.setCurrentRedeem(found);
    } else {
      const partnerName = selectedPartner?.label || '';
      productHook.setCurrentRedeem({
        _id: selectedRedeemId,
        value: selectedRedeemId,
        label: selectedRedeemName,
        name: selectedRedeemName,
        details: {
          name: selectedRedeemName,
        },
        partnerId: {
          _id: partner,
          name: partnerName,
        },
        partner: {
          _id: partner,
          name: partnerName,
        },
      });
    }

    productHook.setOpenRedeemModal(false);
    reset();

    enqueueSnackbar('Resgatável associado com sucesso.', {
      variant: 'info',
      transitionDuration: 2000,
    });
  }, [
    enqueueSnackbar,
    partner,
    productHook,
    reset,
    selectedPartner?.label,
    selectedRedeemId,
    selectedRedeemName,
  ]);

  useEffect(() => {
    if (!partner) {
      resetFilteredRedeems();
      return;
    }

    if (name !== debouncedName && !pageChangedByPagination) {
      return;
    }

    const requestPage = pageChangedByPagination ? page + 1 : 1;

    filterRedeems({
      partners: [partner],
      name: debouncedName,
      page: requestPage,
      limit: DEFAULT_LIMIT,
    });
  }, [
    debouncedName,
    filterRedeems,
    name,
    page,
    pageChangedByPagination,
    partner,
    resetFilteredRedeems,
  ]);

  return {
    partner,
    name,
    page,
    limit: DEFAULT_LIMIT,
    selectedRedeemId,
    selectedRedeemName,
    filteredRedeems,
    filteredRedeemsTotal,
    loadingFilteredRedeems,
    onClose,
    onSelectPartner,
    onSelectRedeem,
    onConfirm,
    onChangeName,
    onChangePage,
    setPage,
    reset,
  };
};
